import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "../components/site/PageHero";
import Metrics from "../components/site/Metrics";
import Testimonials from "../components/site/Testimonials";
import WhyDaos from "../components/site/WhyDaos";
import FinalCTA from "../components/site/FinalCTA";
import { usePageSEO, breadcrumb } from "../hooks/usePageSEO";

const cases = [
  {
    industry: "SaaS · LATAM",
    title: "Centralización omnicanal y respuesta < 5 min",
    summary:
      "Implementación de Respond.io con IA conversacional, integración con CRM y campañas de Meta + Google. El equipo pasó de horas de respuesta a minutos.",
    metrics: [
      { v: "+38%", l: "Conversación-a-cita" },
      { v: "−62%", l: "Tiempo de respuesta" },
      { v: "5 → 1", l: "Bandejas unificadas" },
    ],
  },
  {
    industry: "Educación corporativa",
    title: "Funnel completo con IA + Ads",
    summary:
      "Diseño e implementación de funnel end-to-end: TikTok + Meta para tope de funnel, IA conversacional para calificar y cierre humano. Resultado: pipeline predecible sin depender de personas.",
    metrics: [
      { v: "3.4x", l: "ROAS estable mes a mes" },
      { v: "+47%", l: "Leads calificados" },
      { v: "24/7", l: "Atención automatizada" },
    ],
  },
  {
    industry: "Servicios B2B",
    title: "Optimización de adquisición y ROAS",
    summary:
      "Auditoría profunda de campañas, conversion API server-side, automatización de seguimiento y reactivación de base fría con IA. Sistema operando sin micromanagement.",
    metrics: [
      { v: "−34%", l: "CPL promedio" },
      { v: "+22%", l: "Cierre comercial" },
      { v: "100%", l: "Atribución por canal" },
    ],
  },
  {
    industry: "Retail mediano",
    title: "Implementación quirúrgica de Respond.io",
    summary:
      "Configuración completa de workspace, automatizaciones, IA y reportes. Cero leads perdidos en festivos y trazabilidad por agente y campaña.",
    metrics: [
      { v: "0", l: "Leads perdidos en festivos" },
      { v: "+28%", l: "Repeat customers" },
      { v: "100%", l: "Conversaciones trazadas" },
    ],
  },
];

export default function CasosDeExito() {
  usePageSEO({
    title: "Casos de Éxito y Resultados | Daos Ads",
    description:
      "Resultados reales, métricas verificables y casos de implementaciones de automatización con IA, Respond.io y publicidad digital. +20 automatizaciones implementadas.",
    path: "/casos-de-exito",
    jsonLd: [
      breadcrumb([
        { name: "Inicio", path: "/" },
        { name: "Casos de éxito", path: "/casos-de-exito" },
      ]),
    ],
  });

  return (
    <main>
      <PageHero
        eyebrow="Casos de éxito"
        title="Resultados reales,"
        highlight="no slides bonitos"
        description="Cada implementación tiene un objetivo medible y un sistema que sigue operando después de mí. Aquí algunos casos genéricos por confidencialidad — los nombres reales se comparten en consultoría."
        primaryCta={{ to: "/contacto", label: "Quiero un diagnóstico" }}
      />

      {/* Reuse Metrics counters */}
      <Metrics />

      {/* Case studies grid */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="reveal max-w-2xl">
            <div className="eyebrow mb-5">Estudios de caso</div>
            <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
              Implementaciones <span className="text-gradient">que siguen operando</span> hoy.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {cases.map((c, i) => (
              <article
                key={c.title}
                data-testid={`case-card-${i}`}
                className="reveal grad-border p-7 card-lift"
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00] font-display font-semibold">
                  {c.industry}
                </div>
                <h3 className="mt-2 font-display font-bold text-white text-2xl tracking-tight">{c.title}</h3>
                <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{c.summary}</p>
                <div className="mt-6 grid grid-cols-3 gap-3 pt-5 border-t border-white/10">
                  {c.metrics.map((m) => (
                    <div key={m.l}>
                      <div className="font-display font-extrabold text-2xl text-gradient">{m.v}</div>
                      <div className="text-zinc-500 text-[11px] mt-1 leading-snug">{m.l}</div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
          <p className="mt-6 text-zinc-500 text-xs">
            * Resultados típicos de implementaciones bien ejecutadas. Datos auditables disponibles bajo NDA.
          </p>
        </div>
      </section>

      {/* Reuse testimonials */}
      <Testimonials />

      {/* Reuse Why Daos */}
      <WhyDaos />

      {/* Cross-links */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="reveal max-w-2xl">
            <div className="eyebrow mb-5">Continúa explorando</div>
            <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
              Servicios que <span className="text-gradient">producen estos resultados</span>.
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { to: "/automatizacion-ia", label: "Automatización con IA", desc: "Agentes y flujos que califican y venden 24/7." },
              { to: "/respond-io", label: "Respond.io", desc: "Centralización omnicanal con IA conversacional." },
              { to: "/funnels", label: "Funnels", desc: "Sistema completo de adquisición conectado." },
            ].map((r) => (
              <Link key={r.to} to={r.to} className="reveal grad-border p-6 card-lift block">
                <div className="flex items-center justify-between">
                  <div className="text-white font-display font-semibold">{r.label}</div>
                  <ArrowUpRight className="w-5 h-5 text-zinc-400" />
                </div>
                <p className="mt-2 text-zinc-400 text-sm">{r.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}
